import { useEffect, useRef, useState } from 'react';
import { Capacitor } from '@capacitor/core';
import { THEMES } from './theme';
import { showAppStorePrompt } from './lib/platform';

// ECG shape: [ms_from_beat_start, y_normalized]
// 0 = baseline, -1 = spike up, +1 = spike down
const BEAT_SHAPE = [
  [0, 0], [50, 0],
  [70, -0.13], [100, 0],
  [130, 0], [145, 0.20],
  [160, -1.00],
  [175, 0.60],
  [200, 0], [250, 0],
  [290, -0.26], [360, 0],
  [520, 0],
];
const BEAT_DUR = 520;
const MAX_PTS = 600;
const PT_INTERVAL = 18;

const LINE_CONFIGS = [
  { yFrac: 0.20, ampFrac: 0.040, alpha: 0.18, lw: 1.0, pxPerSec: 90 },
  { yFrac: 0.50, ampFrac: 0.210, alpha: 1.00, lw: 2.6, pxPerSec: 85 },
  { yFrac: 0.80, ampFrac: 0.032, alpha: 0.14, lw: 0.8, pxPerSec: 90 },
];

function getBeatY(cfg, H, ms) {
  const yC = H * cfg.yFrac;
  const amp = H * cfg.ampFrac;
  for (let i = 0; i < BEAT_SHAPE.length - 1; i++) {
    const [t0, dy0] = BEAT_SHAPE[i];
    const [t1, dy1] = BEAT_SHAPE[i + 1];
    if (ms >= t0 && ms <= t1) {
      const t = (ms - t0) / (t1 - t0);
      const e = t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2;
      return yC + (dy0 + (dy1 - dy0) * e) * amp;
    }
  }
  return yC;
}

function drawBloom(ctx, x, y, r, a) {
  const g = ctx.createRadialGradient(x, y, 0, x, y, r);
  g.addColorStop(0, `rgba(139,30,45,${a * 0.5})`);
  g.addColorStop(0.3, `rgba(139,30,45,${a * 0.25})`);
  g.addColorStop(1, 'rgba(139,30,45,0)');
  ctx.fillStyle = g;
  ctx.beginPath();
  ctx.arc(x, y, r, 0, Math.PI * 2);
  ctx.fill();
}

function useHeartbeatCanvas(canvasRef) {
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    let rafId;
    // Reduce Motion: the heartbeat is pure decoration, and an infinite canvas
    // loop is exactly what the setting exists to stop. One frame, no loop.
    const reduceMotion = typeof window.matchMedia === "function" && window.matchMedia("(prefers-reduced-motion: reduce)").matches;

    const lines = LINE_CONFIGS.map((cfg, i) => ({
      cfg, cursorX: 0, pts: [],
      beatPhase: null, nextBeatAt: 150 + i * 70,
      glow: 0, lastTs: null, lastPtMs: 0,
      timeSinceLastBeat: i * 180, // stagger lines slightly
    }));

    function resize() {
      canvas.width = Math.min(window.innerWidth, document.documentElement.clientWidth);
      canvas.height = window.innerHeight;
      lines.forEach((l, i) => {
        l.cursorX = 0; l.pts = []; l.beatPhase = null;
        l.nextBeatAt = 150 + i * 70; l.lastPtMs = 0;
      });
    }
    resize();
    window.addEventListener('resize', resize);

    function draw(ts) {
      if (!canvasRef.current) return; // component unmounted — stop loop
      const W = canvas.width, H = canvas.height;
      ctx.clearRect(0, 0, W, H);

      lines.forEach(line => {
        if (!line.lastTs) line.lastTs = ts;
        const dt = Math.min(ts - line.lastTs, 50);
        line.lastTs = ts;

        const { cfg } = line;
        const yC = H * cfg.yFrac;
        const pxPerMs = cfg.pxPerSec / 1000;
        const isMain = cfg.yFrac === 0.50;

        line.cursorX += dt * pxPerMs;

        if (line.cursorX >= W) {
          line.cursorX = 0; line.pts = [];
          line.nextBeatAt = 120; line.beatPhase = null; line.lastPtMs = 0;
          line.timeSinceLastBeat = 0;
        }

        // Time-based beat: 65 BPM = beat every ~923ms
        if (!line.timeSinceLastBeat) line.timeSinceLastBeat = 0;
        line.timeSinceLastBeat += dt;
        const BEAT_INTERVAL_MS = 923;
        if (line.beatPhase === null && line.timeSinceLastBeat >= BEAT_INTERVAL_MS) {
          line.beatPhase = 0;
          line.timeSinceLastBeat = 0;
          if (isMain) line.glow = 1.0;
        }

        let y = yC;
        if (line.beatPhase !== null) {
          line.beatPhase += dt;
          if (line.beatPhase >= BEAT_DUR) {
            line.beatPhase = null;
            // next beat handled by timeSinceLastBeat timer
          } else {
            y = getBeatY(cfg, H, line.beatPhase);
          }
        }

        line.lastPtMs += dt;
        if (line.lastPtMs >= PT_INTERVAL) {
          line.lastPtMs = 0;
          line.pts.push({ x: line.cursorX, y });
          if (line.pts.length > MAX_PTS) line.pts.shift();
        }

        line.glow *= 0.97;

        const { pts } = line;
        const n = pts.length;
        const BANDS = 4;

        for (let b = 0; b < BANDS; b++) {
          const ageMid = (b + 0.5) / BANDS;
          const iS = Math.floor((b / BANDS) * (n - 1));
          const iE = Math.floor(((b + 1) / BANDS) * (n - 1));
          if (iE <= iS || iS >= n) continue;

          let alpha = Math.pow(ageMid, 2.0) * cfg.alpha;
          if (isMain) alpha += line.glow * 0.32 * ageMid;
          if (alpha < 0.005) continue;

          const isTip = b === BANDS - 1;

          ctx.beginPath();
          ctx.moveTo(pts[iS].x, pts[iS].y);
          for (let i = iS + 1; i <= iE && i < n; i++) ctx.lineTo(pts[i].x, pts[i].y);

          if (isTip && isMain) {
            ctx.shadowColor = `rgba(139,30,45,${0.25 + line.glow * 0.15})`;
            ctx.shadowBlur = 4 + line.glow * 6;
            ctx.lineWidth = cfg.lw * (2.4 + line.glow * 3);
            ctx.strokeStyle = `rgba(139,30,45,${Math.min(alpha * 4, 1)})`;
          } else if (isMain) {
            ctx.shadowColor = `rgba(139,30,45,${0.12 + line.glow * 0.2})`;
            ctx.shadowBlur = 2 + line.glow * 5 + ageMid * 2;
            ctx.lineWidth = cfg.lw * (0.6 + ageMid * 0.9);
            ctx.strokeStyle = `rgba(139,30,45,${alpha})`;
          } else {
            ctx.shadowBlur = 0;
            ctx.lineWidth = cfg.lw;
            ctx.strokeStyle = `rgba(139,30,45,${alpha})`;
          }
          ctx.lineJoin = 'round';
          ctx.lineCap = 'round';
          ctx.stroke();
        }

        ctx.shadowBlur = 0;

        if (isMain && n > 0) {
          const tip = pts[n - 1];
          drawBloom(ctx, tip.x, tip.y, 80 + line.glow * 130, 0.08 + line.glow * 0.42);
          drawBloom(ctx, tip.x, tip.y, 20 + line.glow * 32, 0.3 + line.glow * 0.32);
        }

      });

      if (!reduceMotion) rafId = requestAnimationFrame(draw);
    }

    rafId = requestAnimationFrame(draw);
    return () => { cancelAnimationFrame(rafId); window.removeEventListener('resize', resize); };
  }, [canvasRef]);
}

function useScrollReveal(ref, threshold = 0.2) {
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setVisible(true); },
      { threshold }
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, [ref, threshold]);
  return visible;
}

// The single-number ScoreRing was retired with the hero rewrite: the product
// scores each pillar independently and has no composite total, so the landing
// page must not advertise one. PillarRow mirrors the app's Your Pillars card
// (label, percentage, band-colored bar, tracker count) so what a visitor sees
// here matches what a tester sees after signing up.
// The landing page is always light (its wrapper hardcodes the cream page),
// but T follows the OS appearance since the Phase 7 toggle, so anything
// reading T went dark under OS dark mode (the pillars card rendered
// near-black). Every themed color here reads the LIGHT palette by name;
// colors still come only from src/theme.js. tests/unit/landing-light-only.test.mjs
// keeps T out of this file and its children.
const L = THEMES.light;
function PillarRow({ label, value, meta, visible, delay = 0 }) {
  const band = value >= 70 ? L.success : value >= 40 ? L.warning : L.danger;
  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 6 }}>
        <span style={{ fontSize: 14, color: L.text, fontWeight: 500 }}>{label}</span>
        <span style={{ fontSize: 14, fontWeight: 700, color: band }}>{value}%<span style={{ fontSize: 11, fontWeight: 400, color: L.textMuted, marginLeft: 8 }}>{meta}</span></span>
      </div>
      <div style={{ height: 6, borderRadius: 99, background: L.surfaceAlt, overflow: 'hidden' }}>
        <div style={{
          height: '100%', borderRadius: 99, background: band,
          width: visible ? `${value}%` : '0%',
          transition: `width 1.1s cubic-bezier(0.22,1,0.36,1) ${delay}s`,
        }} />
      </div>
    </div>
  );
}

function PillarCard({ icon, name, desc, delay = 0 }) {
  const ref = useRef(null);
  const visible = useScrollReveal(ref);
  return (
    <div ref={ref} style={{
      background: '#ffffff', border: '1px solid rgba(0,0,0,0.08)',
      borderRadius: 16, padding: 32,
      opacity: visible ? 1 : 0,
      transform: visible ? 'translateY(0)' : 'translateY(30px)',
      transition: `opacity 0.6s ease ${delay}s, transform 0.6s ease ${delay}s, border-color 0.25s`,
    }}
      onMouseEnter={e => e.currentTarget.style.borderColor = 'rgba(139,30,45,0.25)'}
      onMouseLeave={e => e.currentTarget.style.borderColor = 'rgba(0,0,0,0.08)'}
    >
      <div style={{ width: 40, height: 40, borderRadius: 10, background: 'rgba(139,30,45,0.08)', display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: 20 }}>
        {icon}
      </div>
      <div style={{ fontSize: 16, fontWeight: 600, marginBottom: 10 }}>{name}</div>
      <div style={{ fontSize: 14, color: '#5c574e', lineHeight: 1.65 }}>{desc}</div>
    </div>
  );
}

export default function Landing({ onSignIn, onViewDemo }) {
  const canvasRef = useRef(null);
  useHeartbeatCanvas(canvasRef);

  const scoreRef = useRef(null);
  const scoreVisible = useScrollReveal(scoreRef);
  const appStoreRef = useRef(null);
  const appStoreVisible = useScrollReveal(appStoreRef);

  // The iOS app renders this same page for signed-out users, so an App Store
  // CTA here would prompt people to download the app they are standing in —
  // tapping it bounces them out to the App Store and back. In the native app
  // the hero CTA becomes "Get started" (the Auth surface handles both sign-in
  // and create-account) and the bottom install-pitch section is dropped
  // entirely. Capacitor.isNativePlatform() is false on the web.
  const isNativeApp = Capacitor.isNativePlatform();
  // The install pitch shows only on a MOBILE web browser (src/lib/platform.js,
  // 2026-11-02): never in the native app, and not on desktop, where the web
  // app is the product.
  const showInstallPitch = showAppStorePrompt({
    native: isNativeApp,
    userAgent: typeof navigator !== 'undefined' ? navigator.userAgent : '',
    maxTouchPoints: typeof navigator !== 'undefined' ? navigator.maxTouchPoints : 0,
  });

  // Mirrors the app's pillar names and its band coloring (green at 70+, amber
  // at 40 to 69). The 61 is deliberate: an honest mixed picture reads more
  // credible than four green bars.
  const bars = [
    { label: 'Financial Health', value: 61, meta: '4 of 4 trackers' },
    { label: 'Health', value: 78, meta: '3 of 4 trackers' },
    { label: 'Habits', value: 82, meta: '5 habits' },
    { label: 'Wellbeing', value: 70, meta: '2 of 2 trackers' },
  ];

  const pillars = [
    {
      name: 'Habits & Discipline',
      desc: 'Build streaks that stick. Track daily habits, log progress, and watch consistency compound over time.',
      icon: <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#8b1e2d" strokeWidth="1.5"><path d="M9 12l2 2 4-4M7.835 4.697a3.42 3.42 0 001.946-.806 3.42 3.42 0 014.438 0 3.42 3.42 0 001.946.806 3.42 3.42 0 013.138 3.138 3.42 3.42 0 00.806 1.946 3.42 3.42 0 010 4.438 3.42 3.42 0 00-.806 1.946 3.42 3.42 0 01-3.138 3.138 3.42 3.42 0 00-1.946.806 3.42 3.42 0 01-4.438 0 3.42 3.42 0 00-1.946-.806 3.42 3.42 0 01-3.138-3.138 3.42 3.42 0 00-.806-1.946 3.42 3.42 0 010-4.438 3.42 3.42 0 00.806-1.946 3.42 3.42 0 013.138-3.138z"/></svg>,
    },
    {
      name: 'Financial Health',
      desc: 'Monitor income, expenses, debts, and credit score in one place. See your financial picture clearly.',
      icon: <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#8b1e2d" strokeWidth="1.5"><line x1="12" y1="1" x2="12" y2="23"/><path d="M17 5H9.5a3.5 3.5 0 000 7h5a3.5 3.5 0 010 7H6"/></svg>,
    },
    {
      name: 'Physical Health',
      desc: 'Log workouts, track sleep, and monitor health metrics that actually affect how you feel and perform.',
      icon: <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#8b1e2d" strokeWidth="1.5"><path d="M22 12h-4l-3 9L9 3l-3 9H2"/></svg>,
    },
    {
      name: 'Mental Wellbeing',
      desc: 'Track mood, stress, and energy over time. Spot patterns before they become problems.',
      icon: <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#8b1e2d" strokeWidth="1.5"><path d="M20.84 4.61a5.5 5.5 0 00-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 00-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 000-7.78z"/></svg>,
    },
  ];

  return (
    <div style={{ background: '#faf8f3', minHeight: '100vh', fontFamily: "'DM Sans', sans-serif", color: '#1a1a1a', overflowX: 'hidden', margin: 0, padding: 0 }}>
      <canvas ref={canvasRef} style={{ position: 'fixed', top: 0, left: 0, width: '100%', maxWidth: '100vw', height: '100%', display: 'block', pointerEvents: 'none', zIndex: 0, WebkitUserSelect: 'none' }} />

      {/* NAV */}
      <nav style={{ position: 'fixed', top: 0, left: 0, right: 0, zIndex: 100, display: 'flex', alignItems: 'center', justifyContent: 'space-between', paddingTop: 'env(safe-area-inset-top, 20px)', paddingLeft: '20px', paddingRight: '20px', paddingBottom: '20px', background: 'linear-gradient(to bottom, rgba(250,248,243,0.98) 0%, transparent 100%)' }}>
        <div style={{ fontFamily: "'DM Serif Display', serif", fontSize: 22 }}>Scorify</div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
          <button onClick={onViewDemo} style={{ background: 'none', border: 'none', color: '#5c574e', fontSize: 14, cursor: 'pointer', fontFamily: "'DM Sans', sans-serif" }}>View demo</button>
          <button onClick={onSignIn} style={{ border: '1px solid rgba(139,30,45,0.5)', color: '#8b1e2d', background: 'transparent', padding: '8px 18px', borderRadius: 20, fontFamily: "'DM Sans', sans-serif", fontSize: 13, fontWeight: 500, cursor: 'pointer' }}>Sign in</button>
        </div>
      </nav>

      {/* HERO */}
      <section style={{ position: 'relative', zIndex: 10, minHeight: '100vh', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', textAlign: 'center', padding: '120px 40px 80px' }}>
        <div style={{ display: 'inline-flex', alignItems: 'center', gap: 8, border: '1px solid rgba(139,30,45,0.35)', borderRadius: 20, padding: '6px 16px', fontSize: 11, fontWeight: 600, letterSpacing: '1.5px', textTransform: 'uppercase', color: '#8b1e2d', marginBottom: 40, animation: 'lifesynFadeUp 0.6s ease forwards 0.2s', opacity: 0 }}>
          <span style={{ width: 6, height: 6, background: '#8b1e2d', borderRadius: '50%', animation: 'lifesynPulse 2s ease-in-out infinite' }} />
          Your life, tracked
        </div>
        {/* Positioning matches the product: pillars are scored independently
            and there is NO composite total, so the hero must never promise
            one number. Sized down from the old two-word headline so both
            sentences wrap cleanly at phone widths. */}
        <h1 style={{ fontFamily: "'DM Serif Display', serif", fontSize: 'clamp(31px, 6vw, 56px)', lineHeight: 1.15, letterSpacing: '-0.02em', margin: 0, maxWidth: 900, textWrap: 'balance' }}>
          <span style={{ display: 'block', animation: 'lifesynFadeUp 0.7s ease forwards 0.4s', opacity: 0, textWrap: 'balance' }}>Finance apps can't see your body.</span>
          <span style={{ display: 'block', color: L.accent, animation: 'lifesynFadeUp 0.7s ease forwards 0.6s', opacity: 0, textWrap: 'balance' }}>Fitness apps can't see your money.</span>
        </h1>
        <p style={{ fontSize: 17, color: L.textMuted, maxWidth: 480, lineHeight: 1.7, margin: '32px auto 0', animation: 'lifesynFadeUp 0.7s ease forwards 0.85s', opacity: 0 }}>
          Scorify sees all of it, and tells you what it means.
        </p>
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 14, marginTop: 48, animation: 'lifesynFadeUp 0.7s ease forwards 1.05s', opacity: 0 }}>
          {/* No App Store link until the listing exists: the app is in
              TestFlight beta, and the old CTA sent mobile-web visitors to a
              dead App Store page for id6762067289 while the badge below
              claimed "Available now on iOS". Restore the store link (same id)
              when the public listing goes live. Until then the web app IS the
              product for browser visitors. */}
          <button onClick={onSignIn}
            style={{ display: 'inline-flex', alignItems: 'center', gap: 10, background: '#ffffff', color: '#1a1a1a', border: '1px solid rgba(139,30,45,0.6)', borderRadius: 12, padding: '13px 28px', fontFamily: "'DM Sans', sans-serif", fontSize: 15, fontWeight: 600, cursor: 'pointer' }}>
            Get started →
          </button>
          <button onClick={onViewDemo} style={{ background: 'none', border: 'none', fontSize: 13, color: '#5c574e', cursor: 'pointer', fontFamily: "'DM Sans', sans-serif" }}>or view the demo →</button>
        </div>
      </section>

      {/* PILLAR SCORES CARD — the app's Your Pillars view, reproduced: four
          independent scores, band colors, tracker counts. No ring, no total. */}
      <section style={{ position: 'relative', zIndex: 10, padding: '0 24px 100px', display: 'flex', justifyContent: 'center' }}>
        <div ref={scoreRef} style={{ background: L.surface, border: `1px solid ${L.border}`, borderRadius: 20, padding: window.innerWidth < 600 ? 28 : 40, maxWidth: 760, width: '100%', display: 'grid', gridTemplateColumns: window.innerWidth < 600 ? '1fr' : '1fr 1.2fr', gap: window.innerWidth < 600 ? 28 : 48, alignItems: 'center', opacity: scoreVisible ? 1 : 0, transform: scoreVisible ? 'translateY(0)' : 'translateY(40px)', transition: 'opacity 0.7s ease, transform 0.7s ease' }}>
          <div>
            <div style={{ fontSize: 11, letterSpacing: '1.5px', textTransform: 'uppercase', color: L.textDim, fontWeight: 600, marginBottom: 6 }}>Your Pillars</div>
            <div style={{ fontFamily: "'DM Serif Display', serif", fontSize: 28, letterSpacing: -0.5, color: L.text, lineHeight: 1.25 }}>Four scores. No single number.</div>
            <p style={{ fontSize: 14, color: L.textMuted, lineHeight: 1.7, margin: '14px 0 0' }}>
              Each pillar is scored only against what you choose to track. A rough month for your money never hides a strong month at the gym.
            </p>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>
            {bars.map((bar, i) => (
              <PillarRow key={bar.label} label={bar.label} value={bar.value} meta={bar.meta} visible={scoreVisible} delay={0.15 + i * 0.12} />
            ))}
          </div>
        </div>
      </section>

      {/* PILLARS */}
      <section style={{ position: 'relative', zIndex: 10, padding: window.innerWidth < 600 ? '0 20px 80px' : '0 40px 120px', maxWidth: 1100, margin: '0 auto' }}>
        <div style={{ textAlign: 'center', marginBottom: 56 }}>
          <div style={{ fontSize: 11, letterSpacing: '1.5px', textTransform: 'uppercase', color: '#5c574e', fontWeight: 600, marginBottom: 16 }}>Four pillars</div>
          <h2 style={{ fontFamily: "'DM Serif Display', serif", fontSize: 'clamp(32px,4vw,52px)', letterSpacing: -1, lineHeight: 1.1 }}>Everything that makes up a life</h2>
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 16 }}>
          {pillars.map((p, i) => <PillarCard key={p.name} {...p} delay={i * 0.1} />)}
        </div>
      </section>

      {/* APP STORE CTA — install pitch for MOBILE WEB only: meaningless inside
          the installed app (the hero already carries Get started) and off on
          desktop browsers. */}
      {showInstallPitch && (
      <section style={{ position: 'relative', zIndex: 10, padding: window.innerWidth < 600 ? '0 20px 80px' : '0 40px 140px', maxWidth: 1100, margin: '0 auto' }}>
        <div ref={appStoreRef} style={{ background: '#ffffff', border: '1px solid rgba(139,30,45,0.15)', borderRadius: 20, padding: '64px 56px', textAlign: 'center', opacity: appStoreVisible ? 1 : 0, transform: appStoreVisible ? 'translateY(0)' : 'translateY(30px)', transition: 'opacity 0.7s ease, transform 0.7s ease' }}>
          {/* Beta-honest copy: the iPhone app is in TestFlight, not the App
              Store. Restore the store badge, copy and link when the listing
              (id6762067289) goes live. */}
          <div style={{ display: 'inline-flex', alignItems: 'center', gap: 6, background: 'rgba(139,30,45,0.08)', border: '1px solid rgba(139,30,45,0.3)', color: '#8b1e2d', fontSize: 12, fontWeight: 500, padding: '5px 14px', borderRadius: 20, marginBottom: 28 }}>
            <span style={{ width: 6, height: 6, background: '#8b1e2d', borderRadius: '50%' }} />
            iPhone app in private beta
          </div>
          <h2 style={{ fontFamily: "'DM Serif Display', serif", fontSize: 'clamp(36px,5vw,56px)', letterSpacing: -1.5, marginBottom: 16 }}>Start scoring your life.</h2>
          <p style={{ fontSize: 15, color: '#5c574e', maxWidth: 440, margin: '0 auto 40px', lineHeight: 1.65 }}>
            Use Scorify free in your browser today, and join the waitlist to be first in line when the iPhone app opens up.
          </p>
          <a href="/waitlist"
            style={{ display: 'inline-flex', alignItems: 'center', gap: 12, background: '#ffffff', color: '#1a1a1a', border: '1px solid rgba(139,30,45,0.6)', borderRadius: 14, padding: '16px 36px', fontFamily: "'DM Sans', sans-serif", fontSize: 16, fontWeight: 600, cursor: 'pointer', textDecoration: 'none', transition: 'border-color 0.2s, transform 0.2s' }}
            onMouseEnter={e => { e.currentTarget.style.borderColor = '#8b1e2d'; e.currentTarget.style.transform = 'translateY(-2px)'; }}
            onMouseLeave={e => { e.currentTarget.style.borderColor = 'rgba(139,30,45,0.6)'; e.currentTarget.style.transform = 'translateY(0)'; }}>
            Join the waitlist
          </a>
        </div>
      </section>
      )}

      {/* FOOTER */}
      <footer style={{ position: 'relative', zIndex: 10, textAlign: 'center', padding: '24px 40px 40px', color: '#5c574e', fontSize: 12 }}>
        © 2026 Scorify. All rights reserved. · <a href="/privacy.html" style={{ color: '#5c574e', textDecoration: 'none' }}>Privacy Policy</a> · <a href="/terms.html" style={{ color: '#5c574e', textDecoration: 'none' }}>Terms of Service</a>
      </footer>

      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=DM+Sans:wght@300;400;500;600&family=DM+Serif+Display&display=swap');
        @keyframes lifesynFadeUp { to { opacity: 1; transform: translateY(0); } }
        @keyframes lifesynPulse { 0%,100%{opacity:1;transform:scale(1)} 50%{opacity:0.4;transform:scale(0.7)} }
        html,body{margin:0;padding:0;background:#faf8f3}
        *{box-sizing:border-box}
        ::-webkit-scrollbar{width:6px}
        ::-webkit-scrollbar-track{background:#faf8f3}
        ::-webkit-scrollbar-thumb{background:rgba(0,0,0,0.1);border-radius:3px}
      `}</style>
    </div>
  );
}
